import { alpha, ChipProps } from "@mui/material";
import {
  CalendarLayer,
  CrewStatus,
  FieldRequirement,
  InvoicePaymentStatus,
  InvoiceStatus,
  JobStatus,
  JobType,
  RecurrenceEditScope,
  RecurrenceFrequency,
  Role,
  ScheduleCategory,
  TransactionType,
  TruckStatus,
} from "../enums";
import {
  CountryCode,
  CrewAvailability,
  CrewDay,
  CrewDayHours,
  CrewSkillLevel,
  DropdownItem,
  InvoiceStatusTargetActions,
  JobTypeFields,
  SummaryTileColors,
  SummaryTileTone,
} from "../types";
import { theme } from "../utils/theme";
import { US_STATES, CANADA_PROVINCES } from "./states";

export const ACCOUNT_DETAILS_TAB_KEY = "tab";
export const ORGANIZATION_DETAILS_TAB_KEY = "tab";
export const SESSION_STORAGE_USER_KEY = "user";
export const SESSION_STORAGE_DP_USER_KEY = "dp-user";
export const INTUIT_CONNECT_TO_QUICKBOOKS_CALLBACK_SOURCE =
  "intuitQuickbooksOAuthCallback";

export const DATE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "This Quarter",
  "Last Quarter",
  "Last 6 Months",
  "Last 12 Months",
  "This Year",
  "Custom Date",
];

export const WASH_FREQUENCIES: DropdownItem[] = [
  { key: "Daily", value: "daily" },
  { key: "Weekly", value: "weekly" },
  { key: "Biweekly", value: "biweekly" },
  { key: "Monthly", value: "monthly" },
  { key: "Semi-Monthly", value: "semi-monthly" },
  { key: "Semi-Annual", value: "semi-annual" },
  { key: "Yearly", value: "yearly" },
  { key: "7 Days", value: "7days" },
  { key: "13 Days", value: "13days" },
  { key: "30 Days", value: "30days" },
  { key: "181 Days", value: "181days" },
  { key: "Custom", value: "custom" },
];

export const INVOICE_FREQUENCIES: DropdownItem[] = [
  { key: "Daily", value: "daily" },
  { key: "Weekly", value: "weekly" },
  { key: "Biweekly", value: "biweekly" },
  { key: "Monthly", value: "monthly" },
];

export const DUE_UPON: DropdownItem[] = [
  { key: "Immediate Payment", value: "0" },
  { key: "Net 7", value: "7" },
  { key: "Net 14", value: "14" },
  { key: "Net 30", value: "30" },
  { key: "Net 60", value: "60" },
  { key: "Custom", value: "custom" },
];

export const DEFAULT_QUERY_OPTIONS: any = {
  retry: false,
};

export const INVOICE_STATUS_TARGET_ACTIONS_ADMIN: InvoiceStatusTargetActions = {
  [InvoiceStatus.Pending]: {
    defaultAction: InvoiceStatus.SendToQuickbooks,
    targetActions: [InvoiceStatus.SendToCustomer, InvoiceStatus.Cancelled],
  },
  [InvoiceStatus.InProgress]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.SendToCustomer,
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.SendToCustomer]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.OnHold]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.Pending,
      InvoiceStatus.SendToCustomer,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.All]: {
    defaultAction: InvoiceStatus.SendToCustomer,
    targetActions: [
      InvoiceStatus.PaymentReceived,
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.InProgress,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.Cancelled]: {
    defaultAction: InvoiceStatus.Deleted,
  },
  [InvoiceStatus.PaymentReceived]: {
    defaultAction: InvoiceStatus.InProgress,
  },
};

export const INVOICE_STATUS_TARGET_ACTIONS_CUSTOMER: InvoiceStatusTargetActions =
  {
    [InvoiceStatus.InProgress]: {
      defaultAction: InvoiceStatus.PaymentReceived,
    },
    [InvoiceStatus.SendToCustomer]: {
      defaultAction: InvoiceStatus.PaymentReceived,
    },
  };

export const CUSTOMER_INVOICE_STATUS_MAP: Record<string, InvoiceStatus[]> = {
  All: [
    InvoiceStatus.OnHold,
    InvoiceStatus.PaymentReceived,
    InvoiceStatus.InProgress,
    InvoiceStatus.SendToCustomer,
  ],
  Unpaid: [
    InvoiceStatus.OnHold,
    InvoiceStatus.InProgress,
    InvoiceStatus.SendToCustomer,
  ],
  Paid: [InvoiceStatus.PaymentReceived],
};

export const CUSTOMER_INVOICE_STATUS_FILTER_DROPDOWN: DropdownItem[] =
  Object.entries(CUSTOMER_INVOICE_STATUS_MAP).map(([key, values]) => ({
    key,
    value: values.join("|"),
  }));

export const CARD_LOGOS: { [key: string]: string } = {
  Visa: "/card-images/Visa.png",
  Mastercard: "/card-images/MasterCard.png",
  Amex: "/card-images/Amex.png",
  Discover: "/card-images/Discover.png",
  Diners: "/card-images/Diners.png",
  StoredValue: "/card-images/StoredValue.png",
  Other: "/card-images/Other.png",
  JCB: "/card-images/JCB.webp",
  CarteBlanche: "/card-images/CarteBlanche.png",
  Interac: "/card-images/Interac.png",
  UnionPay: "/card-images/UnionPay.png",
};

export const STATUS_LABELS: Record<string, string> = {
  [InvoiceStatus.All]: "All",
  [InvoiceStatus.Pending]: "Pending",
  [InvoiceStatus.SendToCustomer]: "Sent to Customer",
  [InvoiceStatus.SendToQuickbooks]: "Sent to Quickbooks",
  [InvoiceStatus.Cancelled]: "Cancelled",
  [InvoiceStatus.Deleted]: "Deleted",
  [InvoiceStatus.OnHold]: "On Hold",
  [InvoiceStatus.PaymentReceived]: "Payment Received",
  [InvoiceStatus.InProgress]: "In Progress",
};

export const PAYMENT_STATUS_LABELS: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.FullyPaid]: "Paid",
  [InvoicePaymentStatus.PartiallyPaid]: "Partial Payment",
  [InvoicePaymentStatus.Overdue]: "Past Due",
  [InvoicePaymentStatus.Unpaid]: "Not Paid",
};

export const PAYMENT_STATUS_COLORS: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.FullyPaid]: "#2e7d32",
  [InvoicePaymentStatus.PartiallyPaid]: "#eb6f10",
  [InvoicePaymentStatus.Overdue]: "#ed0202",
  [InvoicePaymentStatus.Unpaid]: "#575554",
};

export const PAYMENT_STATUS_TILES = [
  {
    key: InvoicePaymentStatus.FullyPaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.FullyPaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.FullyPaid],
  },
  {
    key: InvoicePaymentStatus.PartiallyPaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.PartiallyPaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.PartiallyPaid],
  },
  {
    key: InvoicePaymentStatus.Overdue,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.Overdue],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.Overdue],
  },
  {
    key: InvoicePaymentStatus.Unpaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.Unpaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.Unpaid],
  },
];
export const PAYMENT_TRANSACTION_TILES = [
  {
    key: TransactionType.Sale,
    title: "Payments",
    color: "#3285bd",
  },
  {
    key: TransactionType.Refund,
    title: "Refunds",
    color: "#ef4444",
  },
];
export const COUNTRY_CONFIG: Record<
  CountryCode,
  {
    stateLabel: string;
    zipLabel: string;
    statesDropdown: DropdownItem[];
  }
> = {
  US: {
    stateLabel: "State",
    zipLabel: "ZIP Code",
    statesDropdown: US_STATES,
  },
  CA: {
    stateLabel: "Province",
    zipLabel: "Postal Code",
    statesDropdown: CANADA_PROVINCES,
  },
};
export const INVOICE_STATUS_DONUT_CONFIG = [
  {
    key: InvoiceStatus.PaymentReceived,
    label: "Payment Received",
    color: "#3ca042",
  },
  {
    key: InvoiceStatus.SendToCustomer,
    label: "Sent to Customer",
    color: "#3971ae",
  },
  {
    key: InvoiceStatus.InProgress,
    label: "In Progress",
    color: "#de902b",
  },
  {
    key: InvoiceStatus.Pending,
    label: "Pending",
    color: "#d04e44",
  },
];

export const CREW_DAYS: CrewDay[] = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

export const CREW_MAX_WEEKLY_HOURS = 48;

export const CREW_EMPTY_AVAILABILITY: CrewAvailability = {
  Mon: null,
  Tue: null,
  Wed: null,
  Thu: null,
  Fri: null,
  Sat: null,
  Sun: null,
};

export const CREW_DEFAULT_DAY_HOURS: CrewDayHours = {
  start: "09:00",
  end: "17:00",
};

export const CREW_TIME_SLOT_MINUTES = 30;

export const CREW_TIME_SLOTS: DropdownItem[] = Array.from(
  { length: (24 * 60) / CREW_TIME_SLOT_MINUTES },
  (_, index) => {
    const totalMinutes = index * CREW_TIME_SLOT_MINUTES;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = String(totalMinutes % 60).padStart(2, "0");

    return {
      key: `${hours % 12 || 12}:${minutes} ${hours < 12 ? "AM" : "PM"}`,
      value: `${String(hours).padStart(2, "0")}:${minutes}`,
    };
  },
);

export const CREW_OVERTIME_MULTIPLIER = 1.5;

export const CREW_ROLE_LABELS: Record<string, string> = {
  [Role.SuperAdmin]: "Super Admin",
  [Role.Admin]: "Admin",
  [Role.Lead]: "Lead",
  [Role.Washer]: "Tech",
  [Role.Driver]: "Driver",
  [Role.Customer]: "Customer",
};

export const CREW_ROLES: Role[] = [
  Role.Admin,
  Role.Lead,
  Role.Washer,
  Role.Driver,
];

export const CREW_ROLES_WITHOUT_EMAIL: Role[] = [Role.Washer, Role.Driver];

export const CREW_ROLES_WITH_REQUIRED_PAY_RATES: Role[] = [
  Role.Washer,
  Role.Driver,
];

export const CREW_ROLES_WITHOUT_CAN_DRIVE: Role[] = [Role.Admin, Role.Lead];

export const CREW_ROLE_DROPDOWN: DropdownItem[] = CREW_ROLES.map((role) => ({
  key: CREW_ROLE_LABELS[role],
  value: role,
}));

export const CREW_ROLE_CHIP_COLORS: Record<string, ChipProps["color"]> = {
  [Role.SuperAdmin]: "error",
  [Role.Admin]: "info",
  [Role.Lead]: "success",
  [Role.Washer]: "primary",
  [Role.Driver]: "secondary",
  [Role.Customer]: "warning",
};

export const CREW_STATUS_CHIP_COLORS: Record<CrewStatus, ChipProps["color"]> = {
  [CrewStatus.Active]: "success",
  [CrewStatus.OnLeave]: "warning",
  [CrewStatus.Inactive]: "default",
};

export const CREW_ALL_FILTER = "All";

export const CREW_STATUS_FILTERS = [
  CREW_ALL_FILTER,
  ...Object.values(CrewStatus),
].map((status) => ({ label: status, value: status }));

export const CREW_VISIBLE_SKILLS = 2;

export const CREW_STATUS_DROPDOWN: DropdownItem[] = Object.values(
  CrewStatus,
).map((status) => ({ key: status, value: status }));

export const CREW_DEFAULT_SKILL_LEVEL: CrewSkillLevel = "beginner";

export const CREW_SKILL_LEVELS: {
  key: string;
  value: CrewSkillLevel;
}[] = [
  { key: "Trainee", value: "trainee" },
  { key: "Beginner", value: "beginner" },
  { key: "Intermediate", value: "intermediate" },
  { key: "Expert", value: "expert" },
];

export const CREW_SKILL_COLORS: Record<
  string,
  { background: string; color: string }
> = {
  "Full Detail": { background: "#DBEAFE", color: "#1D4ED8" },
  Express: { background: "#D1FAE5", color: "#065F46" },
  "Premium Wash": { background: "#EDE9FE", color: "#5B21B6" },
  Waterless: { background: "#E0F2FE", color: "#0369A1" },
  "Interior Only": { background: "#FEF3C7", color: "#92400E" },
  "Engine Bay": { background: "#FCE7F3", color: "#9D174D" },
};

export const TRUCK_ALL_FILTER = "All";

export const TRUCK_STATUS_LABELS: Record<TruckStatus, string> = {
  [TruckStatus.Active]: "Active",
  [TruckStatus.Available]: "Available",
  [TruckStatus.InMaintenance]: "In Maintenance",
  [TruckStatus.OutOfService]: "Out of Service",
};

export const TRUCK_STATUS_FILTERS = [
  { label: TRUCK_ALL_FILTER, value: TRUCK_ALL_FILTER },
  ...Object.values(TruckStatus).map((status) => ({
    label: TRUCK_STATUS_LABELS[status],
    value: status,
  })),
];

export const TRUCK_STATUS_CHIP_COLORS: Record<TruckStatus, ChipProps["color"]> =
  {
    [TruckStatus.Active]: "success",
    [TruckStatus.Available]: "info",
    [TruckStatus.InMaintenance]: "warning",
    [TruckStatus.OutOfService]: "error",
  };

export const TRUCK_CAPACITY_UNIT = "gal";

export const TRUCK_STATUS_DROPDOWN: DropdownItem[] = Object.values(
  TruckStatus,
).map((status) => ({ key: TRUCK_STATUS_LABELS[status], value: status }));

export const TRUCK_UNASSIGNED_DRIVER = "";

export const PAY_RATE_ALL_FILTER = "All";

export const PAY_RATE_ROLE_FILTERS = [
  { label: PAY_RATE_ALL_FILTER, value: PAY_RATE_ALL_FILTER },
  ...CREW_ROLES_WITH_REQUIRED_PAY_RATES.map((role) => ({
    label: CREW_ROLE_LABELS[role],
    value: role,
  })),
];

export const SUMMARY_TILE_TONES: Record<SummaryTileTone, SummaryTileColors> = {
  neutral: {
    background: "var(--dashboard-surface)",
    border: "var(--dashboard-border-tile)",
    value: "var(--dashboard-text-primary)",
  },
  primary: {
    background: alpha(theme.palette.primary.main, 0.06),
    border: alpha(theme.palette.primary.main, 0.2),
    value: theme.palette.primary.main,
  },
  success: {
    background: "var(--dashboard-success-tint)",
    border: "var(--dashboard-success-border)",
    value: "var(--dashboard-success)",
  },
  info: {
    background: "var(--dashboard-info-light)",
    border: "var(--dashboard-info-border)",
    value: "var(--dashboard-info)",
  },
  warning: {
    background: alpha(theme.palette.warning.main, 0.06),
    border: alpha(theme.palette.warning.main, 0.2),
    value: theme.palette.warning.main,
  },
  danger: {
    background: "var(--dashboard-danger-light)",
    border: alpha(theme.palette.error.main, 0.2),
    value: "var(--dashboard-danger)",
  },
};

export const CREW_ROLE_TILE_TONES: Partial<Record<Role, SummaryTileTone>> = {
  [Role.Washer]: "primary",
  [Role.Driver]: "warning",
};

export const CALENDAR_START_HOUR = 0;

export const CALENDAR_END_HOUR = 24;

export const CALENDAR_SCROLL_TO_HOUR = 7;

export const CALENDAR_DEFAULT_START_TIME = `${String(CALENDAR_SCROLL_TO_HOUR).padStart(2, "0")}:00`;

export const CALENDAR_GRID_MAX_HEIGHT = 640;

export const CALENDAR_DEFAULT_TIMEZONE = "America/Phoenix";

export const CALENDAR_NO_ACCESS_MESSAGE =
  "You do not have permission to view the SprayzTime schedule.";

export const CALENDAR_HOUR_HEIGHT = 56;

export const CALENDAR_SLOT_MINUTES = 30;

export const CALENDAR_DATE_FORMAT = "YYYY-MM-DD";

export const CALENDAR_TIME_SLOTS: DropdownItem[] = CREW_TIME_SLOTS.filter(
  (slot) =>
    String(slot.value) >=
      `${String(CALENDAR_START_HOUR).padStart(2, "0")}:00` &&
    String(slot.value) < `${String(CALENDAR_END_HOUR).padStart(2, "0")}:00`,
);

export const CALENDAR_LAYERS: { label: string; value: CalendarLayer }[] = [
  { label: "Jobs", value: CalendarLayer.Jobs },
  { label: "Trucks", value: CalendarLayer.Trucks },
  { label: "Crew", value: CalendarLayer.Crew },
];

export const CALENDAR_CREW_ROLES: Role[] = [
  Role.Lead,
  Role.Washer,
  Role.Driver,
];

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  [JobStatus.Scheduled]: "Scheduled",
  [JobStatus.Confirmed]: "Confirmed",
  [JobStatus.InProgress]: "In Progress",
  [JobStatus.Completed]: "Completed",
  [JobStatus.Skipped]: "Skipped",
  [JobStatus.Canceled]: "Cancelled",
};

export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  [JobStatus.Scheduled]: "var(--dashboard-info)",
  [JobStatus.Confirmed]: theme.palette.primary.main,
  [JobStatus.InProgress]: theme.palette.warning.main,
  [JobStatus.Completed]: "var(--dashboard-success)",
  [JobStatus.Skipped]: "var(--dashboard-text-secondary)",
  [JobStatus.Canceled]: "var(--dashboard-danger)",
};

export const JOB_STATUS_DROPDOWN: DropdownItem[] = Object.values(JobStatus)
  .filter((status) => status !== JobStatus.Confirmed)
  .map((status) => ({ key: JOB_STATUS_LABELS[status], value: status }));

export const JOB_INACTIVE_STATUSES: JobStatus[] = [
  JobStatus.Skipped,
  JobStatus.Canceled,
];

export const JOB_DURATION_DROPDOWN: DropdownItem[] = Array.from(
  { length: 16 },
  (_, index) => {
    const minutes = (index + 1) * CALENDAR_SLOT_MINUTES;
    const hours = minutes / 60;

    return {
      key:
        minutes < 60
          ? `${minutes} min`
          : `${hours} hour${hours === 1 ? "" : "s"}`,
      value: minutes,
    };
  },
);

export const JOB_CUSTOM_OPTION = "custom";

export const JOB_TIME_FORMAT = "HH:mm";

export const JOB_TIME_LABEL_FORMAT = "h:mm A";

export const JOB_TIME_INPUT_FORMAT = "hh:mm A";

export const JOB_DURATION_INPUT_FORMAT = "HH:mm";

export const JOB_DRAG_SNAP_MINUTES = 15;

export const JOB_DRAG_THRESHOLD_PX = 4;

export const JOB_UNASSIGNED_TRUCK = "";

export const JOB_UNKNOWN_TRUCK_LABEL = "Assigned truck";

export const TRUCK_UNAVAILABLE_STATUSES: TruckStatus[] = [
  TruckStatus.InMaintenance,
  TruckStatus.OutOfService,
];

export const RECURRENCE_FREQUENCY_DROPDOWN: DropdownItem[] = Object.values(
  RecurrenceFrequency,
).map((frequency) => ({ key: frequency, value: frequency }));

export const RECURRENCE_WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

export const RECURRENCE_WORKING_WEEKDAYS = [1, 2, 3, 4, 5];

export const RECURRENCE_WEEK_ORDINALS = [
  "first",
  "second",
  "third",
  "fourth",
  "last",
];

export const RECURRENCE_CUSTOM_OPTION = "custom";

export const RECURRENCE_MAX_INTERVAL = 99;

export const RECURRENCE_MAX_OCCURRENCES = 999;

export const RECURRENCE_DEFAULT_OCCURRENCES = 13;

export const RECURRENCE_DEFAULT_END_MONTHS = 3;

export const RECURRENCE_UNSUPPORTED_MESSAGE =
  "This repeat option can't be saved yet";

export const JOB_RECURRENCE_CUSTOM_MIN_DAYS = 2;

export const JOB_RECURRENCE_CUSTOM_MAX_DAYS = 365;

export const RECURRENCE_SCOPE_OPTIONS: {
  label: string;
  value: RecurrenceEditScope;
}[] = [
  { label: "This job", value: RecurrenceEditScope.ThisOccurrence },
  {
    label: "This job and future jobs",
    value: RecurrenceEditScope.ThisAndFuture,
  },
];

export const RECURRENCE_EDIT_SCOPE_OPTIONS: {
  label: string;
  value: RecurrenceEditScope;
}[] = [
  ...RECURRENCE_SCOPE_OPTIONS,
  {
    label: "Entire recurring series",
    value: RecurrenceEditScope.EntireSeries,
  },
];

export const RECURRENCE_ENTIRE_SERIES_UNSUPPORTED_MESSAGE =
  "Updating the entire series isn't available yet";

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.Job]: "Job",
  [JobType.Quote]: "Quote",
  [JobType.DemoWash]: "Demo Wash",
  [JobType.TravelTime]: "Travel Time",
  [JobType.Break]: "Break",
  [JobType.ShopTime]: "Shop Time",
  [JobType.Training]: "Training",
  [JobType.FuelStop]: "Fuel Stop",
  [JobType.Custom]: "Custom",
};

export const SCHEDULE_CATEGORY_LABELS: Record<ScheduleCategory, string> = {
  [ScheduleCategory.Job]: "Job",
  [ScheduleCategory.Event]: "Event",
};

export const SCHEDULE_CATEGORY_OPTIONS = Object.values(ScheduleCategory).map(
  (category) => ({
    label: SCHEDULE_CATEGORY_LABELS[category],
    value: category,
  }),
);

export const JOB_DEFAULT_EVENT_TYPE = JobType.Quote;

export const JOB_EVENT_TYPE_DROPDOWN: DropdownItem[] = Object.values(JobType)
  .filter((type) => type !== JobType.Job)
  .map((type) => ({ key: JOB_TYPE_LABELS[type], value: type }));

const EVENT_FIELDS: JobTypeFields = {
  customer: FieldRequirement.Hidden,
  address: FieldRequirement.Hidden,
  truck: FieldRequirement.Optional,
  crew: FieldRequirement.Required,
  title: FieldRequirement.Hidden,
  recurrence: FieldRequirement.Hidden,
};

const VISIT_EVENT_FIELDS: JobTypeFields = {
  ...EVENT_FIELDS,
  customer: FieldRequirement.Optional,
  address: FieldRequirement.Required,
  crew: FieldRequirement.Optional,
};

export const JOB_TYPE_FIELDS: Record<JobType, JobTypeFields> = {
  [JobType.Job]: {
    customer: FieldRequirement.Required,
    address: FieldRequirement.Required,
    truck: FieldRequirement.Optional,
    crew: FieldRequirement.Optional,
    title: FieldRequirement.Hidden,
    recurrence: FieldRequirement.Optional,
  },
  [JobType.Quote]: VISIT_EVENT_FIELDS,
  [JobType.DemoWash]: VISIT_EVENT_FIELDS,
  [JobType.TravelTime]: { ...EVENT_FIELDS, address: FieldRequirement.Optional },
  [JobType.Break]: { ...EVENT_FIELDS, truck: FieldRequirement.Hidden },
  [JobType.ShopTime]: EVENT_FIELDS,
  [JobType.Training]: { ...EVENT_FIELDS, truck: FieldRequirement.Hidden },
  [JobType.FuelStop]: {
    ...EVENT_FIELDS,
    address: FieldRequirement.Optional,
    truck: FieldRequirement.Required,
  },
  [JobType.Custom]: {
    ...EVENT_FIELDS,
    address: FieldRequirement.Optional,
    title: FieldRequirement.Required,
  },
};

export const TERMS_REQUIRED_ROLES = [Role.Admin, Role.Customer];

export const TERMS_TITLES: Partial<Record<Role, string>> = {
  [Role.Admin]: "Admin User Terms of Service",
  [Role.Customer]: "Customer Portal User Terms of Service",
};

export const TERMS_SCROLL_END_THRESHOLD = 24;
